import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { Directive, ElementRef, Inject, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

import { EventBus, RoutingService } from '@skylab-angular-shared/core';
import { EventBusBase } from '@skylab-shared/core';
import { BrowserIdentifier, isFunction, ListenersRegistry } from '@skylab-shared/utils';

import { $, $Element } from '../utils/element';
import { Menu, MenuItem, MenuItemOption, MenuType } from './menu.model';
import { MenuService } from './menu.service';

export interface DisplayedMenuItem {
    label: string;
    icon: string;
    separator: boolean;
    submenu: DisplayedMenuItem[];
    disabled: boolean;
    onClick: (context: unknown) => void;
    isActive: boolean;
}

@Directive({ selector: '[skl-menu]' })
export class MenuDirective extends ListenersRegistry(class { }) implements OnInit, OnDestroy {
    // bindings
    @Input('skl-menu') public menu!: Menu<unknown> | undefined;
    @Input('skl-menu-context') public menuContext: unknown;

    // other
    private closeFn?: () => void;
    private currentItems!: MenuItem[];

    // events
    private clickEventName = `click.${uuid()}`;
    private contextmenuEventName = `contextmenu.${uuid()}`;

    private eventsBound!: boolean;
    private destroy$ = new Subject<void>();

    // element
    private element: $Element;
    public constructor(
        private elRef: ElementRef,
        private menuService: MenuService,
        private readonly routingService: RoutingService,
        @Inject(EventBus) public eventBus: EventBusBase
    ) {
        super();
        this.element = $(this.elRef.nativeElement);
    }

    public ngOnInit(): void {
        this.bindEvents();
        this.currentItems = this.getDefaultItems(this.menu?.items);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['menu'] && !changes['menu'].firstChange && this.menu?.type === MenuType.Options) {
            this.currentItems = this.getDefaultItems(this.menu.items);
        }
    }

    public ngOnDestroy(): void {
        this.deregisterAllListeners();
        this.destroy$?.next();
    }

    private getDefaultItems(items: MenuItem[] | undefined): MenuItem[] {
        if (!items) return [];

        const collection: MenuItem[] = [];

        items.forEach(elem => {
            const item = elem instanceof MenuItemOption
                ? new MenuItemOption({label: elem.label, click: elem.click, disabled: elem.disabled, icon: elem.icon, separator: elem.separator, submenu: undefined, disableIcon: elem.disableIcon, identityName: elem.identityName, isChecked: elem.isChecked})
                : new MenuItem({dynamicLabel: elem.dynamicLabel, label: elem.label, click: elem.click, disabled: elem.disabled, icon: elem.icon, separator: elem.separator, submenu: undefined});

            if (elem.submenu) {
                item.submenu = this.getDefaultItems(elem.submenu);
            }
            collection.push(item);
        });

        return collection;
    }

    private triggerClose(): void {
        if (isFunction(this.closeFn)) {
            this.closeFn();
            this.closeFn = undefined;
        }
    }

    //#region - collection

    private getContext(): unknown {
        return isFunction(this.menu?.getContext)
            ? this.menu.getContext()
            : this.menuContext;
    }

    private getCollection(): MenuItem[] {
        const items = this.getDefaultItems(this.menu?.type === MenuType.Options ? this.currentItems : this.menu?.items);

        if (this.menu?.showDisabled)
            return items;

        const filterDisabled = (item: MenuItem) => {
            if (!item.isDisabled(this.getContext()) && item.submenu) {
                item.submenu = item.submenu.filter(filterDisabled);
            }
            return !item.isDisabled(this.getContext());
        };

        const setSeparator = (index: number) => {
            if (items[index]?.isDisabled(this.getContext()) && items[index]?.separator && items[index - 1]) {
                items[index - 1].separator = true;
                setSeparator(index - 1);
            }
        };
        items.forEach((_, index) => setSeparator(index));
        return items.filter(filterDisabled);
    }

    //#endregion

    //#region - events

    protected bindEvents(): void {
        if (this.menu && !this.eventsBound) {
            switch (this.menu.type) {
                case MenuType.Context:
                    this.element.on(this.contextmenuEventName, this.onOpenContext.bind(this));
                    break;
                case MenuType.Options:
                    this.element.on(this.contextmenuEventName, this.onOpenOptions.bind(this));
                    break;
                case MenuType.Pointer:
                    this.element.on(this.clickEventName, this.onOpenPointer.bind(this));
                    break;
            }

            this.routingService.states$.pipe(takeUntil(this.destroy$)).subscribe(_ => {
                this.triggerClose();
            });

            this.registerListening(() => {
                this.element.off(this.contextmenuEventName);
                this.element.off(this.clickEventName);
            });
            this.eventsBound = true;
        }
    }

    //#endregion

    //#region - opening
    public onOpenPointer(): void {
        if (this.closeFn) {
            this.triggerClose();
        } else {
            setTimeout(() => { // at the end of event

                this.currentItems = this.getCollection();
                if (!this.currentItems.length) return;

                this.closeFn = this.menuService.showMenuPointer(
                    this.element,
                    this.currentItems,
                    this.getContext(),
                    ($event?: Event) => {
                        const focusEvent = $event as FocusEvent;
                        // checks if blur on menu-pointer is coused by outside click or click on skl-menu directive element
                        // click on skl-menu directive is handled by onOpenPointer method rather than some general behaviour
                        // this functionality is necessary to make toggling on menu-pointer work
                        if (
                            !(
                                (focusEvent && focusEvent.relatedTarget === this.element.element) // normal browsers
                                || (document.activeElement === this.element.element) // IE11
                            )
                        ) {
                            this.closeFn = undefined;
                        }
                    }
                );
            });
        }
    }

    public onOpenContext($event?: Event): void {
        if (!$event) return;
        const mouseEvent = $event as MouseEvent;
        mouseEvent.preventDefault();
        if ((BrowserIdentifier.isChrome() && mouseEvent.which === 0) ||
            (BrowserIdentifier.isFirefox() && mouseEvent.which === 1)) {
            // contextmenu triggered by keyboard
            this.element.find('.js-menu-context-by-keyboard').click();
            // then find three dot element to trigger on him click
            return;
        }
        setTimeout(() => { // at the end of event
            this.triggerClose();

            this.currentItems = this.getCollection();
            if (!this.currentItems.length) return;

            this.closeFn = this.menuService.showMenuContext(
                this.element,
                this.currentItems,
                this.getContext(),
                { left: mouseEvent.clientX, top: mouseEvent.clientY },
            );
        });
    }

    public onOpenOptions($event?: Event): void {
        if (!$event) return;
        const mouseEvent = $event as MouseEvent;
        mouseEvent.preventDefault();
        setTimeout(() => { // at the end of event
            this.triggerClose();

            this.currentItems = this.getCollection();
            if (!this.currentItems.length) return;

            this.closeFn = this.menuService.showMenuOptions(
                this.element,
                this.currentItems,
                { left: mouseEvent.clientX, top: mouseEvent.clientY },
            );
        });

    }

    //#endregion

}
